import type { NiatSource } from '@iqrapath/shared'
import { useQueryClient } from '@tanstack/react-query'
import { Check, ChevronLeft } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { SourceLink } from '../../components/SourceLink.tsx'
import { ApiError } from '../../lib/api.ts'
import { addNiat, refreshPerjalanan, type Hadis } from './perjalanan.ts'

/** A niat is at most 160 characters; longer steps are cut at a word. */
export const clipNiat = (s: string) => (s.length <= 160 ? s : `${s.slice(0, s.lastIndexOf(' ', 157) > 80 ? s.lastIndexOf(' ', 157) : 157)}…`)

/** "Jadikan niat": puts a small step into Niat & Amal, with where it came from. */
export function JadikanNiat({ text, source, sourceRef, label = 'Jadikan niat' }: { text: string; source: NiatSource; sourceRef: string; label?: string }) {
  const qc = useQueryClient()
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [error, setError] = useState('')
  const save = async () => {
    setError('')
    setState('saving')
    try {
      await addNiat(clipNiat(text), source, sourceRef)
      setState('saved')
      void refreshPerjalanan(qc)
    } catch (e) {
      setError(e instanceof ApiError && e.status !== 0 ? e.message : 'Belum tersimpan. Cek koneksi, lalu coba lagi.')
      setState('idle')
    }
  }
  if (state === 'saved') return <span className="niat-ok" role="status"><Check size={14} aria-hidden="true" /> Masuk ke <Link to="/perjalanan/niat">Niat &amp; Amal</Link></span>
  return (
    <>
      <button type="button" className="link-btn niat-btn" onClick={() => void save()} disabled={state === 'saving'} aria-label={`${label}: ${clipNiat(text)}`}>{label}</button>
      {error && <span className="error niat-err" role="alert">{error}</span>}
    </>
  )
}

export function Back({ to = '/perjalanan', label = 'Perjalanan' }: { to?: string; label?: string }) {
  return <Link className="back" to={to}><ChevronLeft size={18} aria-hidden="true" /> {label}</Link>
}

/** A Sahih hadith exactly as the dataset has it (HadeethEnc terms: no changes), with grade, narrator and source link. */
export function HadisBlock({ h }: { h: Hadis }) {
  return (
    <figure className="pg-hadis">
      <span className="pill pill-mint">{h.grade}</span>
      <blockquote>{h.text}</blockquote>
      <figcaption>
        <span>{h.attribution}</span>
        <SourceLink href={h.url}>{h.source}</SourceLink>
      </figcaption>
    </figure>
  )
}
